import Swiper from 'swiper/bundle';
import 'swiper/css/bundle';
import gsap from 'gsap';

const initSwiper = ( selector, options ) => {
	const element = document.querySelector( selector );

	if ( ! element ) {
		return;
	}

	return new Swiper( element, options );
};

const prefersReducedMotion = () =>
	window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

const initProjectsFilter = () => {
	const section = document.querySelector( '[data-projects-filter]' );
	const list = section?.querySelector( '.all-projects__list' );
	const filterButtons = [
		...( section?.querySelectorAll( '[data-project-filter]' ) ?? [] ),
	];
	const cards = [
		...( section?.querySelectorAll( '[data-project-card]' ) ?? [] ),
	];
	const cta = section?.querySelector( '[data-project-static-card]' );
	const sentinel = section?.querySelector( '[data-projects-sentinel]' );

	if ( ! section || ! list || ! cards.length || ! cta || ! sentinel ) {
		return;
	}

	const pageSize = Math.max(
		1,
		Number.parseInt( section.dataset.projectsPageSize ?? '4', 10 ) || 4
	);

	let activeFilter = 'all';
	let visibleCount = pageSize;
	let isLoading = false;

	const getMatches = ( filter ) => {
		if ( filter === 'all' ) {
			return cards;
		}

		return cards.filter( ( card ) => {
			const tags = ( card.dataset.projectTags ?? '' )
				.trim()
				.split( /\s+/ )
				.filter( Boolean );

			return tags.includes( filter );
		} );
	};

	const animateCards = ( elements ) => {
		if ( ! elements.length || prefersReducedMotion() ) {
			return;
		}

		gsap.from( elements, {
			opacity: 0,
			y: 24,
			duration: 0.4,
			stagger: 0.07,
			ease: 'power2.out',
			clearProps: 'opacity,transform',
		} );
	};

	const render = ( { animate = true } = {} ) => {
		const matches = getMatches( activeFilter );
		const newlyShown = [];

		cards.forEach( ( card ) => {
			const matchIndex = matches.indexOf( card );
			const shouldShow = matchIndex !== -1 && matchIndex < visibleCount;
			const wasHidden = card.hasAttribute( 'hidden' );

			if ( shouldShow ) {
				card.hidden = false;

				if ( wasHidden ) {
					newlyShown.push( card );
				}
			} else {
				card.hidden = true;
			}
		} );

		list.appendChild( cta );

		const hasMore = visibleCount < matches.length;
		sentinel.hidden = ! hasMore;
		isLoading = false;

		if ( animate ) {
			animateCards( newlyShown );
		}
	};

	const setFilter = ( filter ) => {
		activeFilter = filter;
		visibleCount = pageSize;

		filterButtons.forEach( ( button ) => {
			const isActive = button.dataset.projectFilter === filter;
			button.classList.toggle( 'is-active', isActive );
			button.setAttribute( 'aria-pressed', isActive ? 'true' : 'false' );
		} );

		cards.forEach( ( card ) => {
			card.hidden = true;
		} );

		render( { animate: true } );
	};

	const loadMore = () => {
		const matches = getMatches( activeFilter );

		if ( isLoading || visibleCount >= matches.length ) {
			return;
		}

		isLoading = true;
		visibleCount += pageSize;
		render( { animate: true } );
	};

	filterButtons.forEach( ( button ) => {
		button.addEventListener( 'click', () => {
			const filter = button.dataset.projectFilter ?? 'all';

			if ( filter === activeFilter ) {
				return;
			}

			setFilter( filter );
		} );
	} );

	const observer = new window.IntersectionObserver(
		( entries ) => {
			entries.forEach( ( entry ) => {
				if ( entry.isIntersecting ) {
					loadMore();
				}
			} );
		},
		{
			rootMargin: '200px 0px',
			threshold: 0,
		}
	);

	observer.observe( sentinel );
	setFilter( activeFilter );
	section.classList.remove( 'is-projects-pending' );
};

document.addEventListener( 'DOMContentLoaded', () => {
	initSwiper( '.all-projects__categories', {
		slidesPerView: 'auto',
		spaceBetween: 20,
		freeMode: true,
		breakpoints: {
			961: {
				spaceBetween: 20,
			},
			361: {
				spaceBetween: 16,
			},
		},
	} );

	initProjectsFilter();
} );
